import { logger } from "@/utils/logger";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IsolatedDatePicker } from '@/components/ui/isolated-datepicker';
import { IsolatedTimePicker } from '@/components/ui/isolated-timepicker';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { createStandardizedFormSubmission } from '@/utils/formSubmissionUtils';
import CollapsibleOpeningHours from '@/components/messages/CollapsibleOpeningHours';
import { FormField } from './shared/FormField';
import type { DepartmentHours } from '@/utils/messageParser/types';

interface ServiceFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  openingHours?: DepartmentHours[];
  title?: string;
}

const ServiceForm: React.FC<ServiceFormProps> = ({
  formData,
  onUpdateData,
  currentStep,
  onStepChange,
  onSubmit,
  openingHours,
}) => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const totalSteps = 2;
  
  // Uncontrolled input refs for name and phone
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  
  // Sync local state when formData changes but stop once user starts typing
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);
  
  const [timeSlots, setTimeSlots] = useState<Array<{value: string, label: string, time: string}>>(
    () => {
      function getDefaultTimeSlots() {
        return [
          { value: 'early_am', label: 'Early', time: '8:00-10:00am' },
          { value: 'am', label: 'AM', time: '10:00-12:00pm' },
          { value: 'pm', label: 'PM', time: '1:00-4:00pm' },
          { value: 'late_pm', label: 'Late', time: '4:00-6:00pm' }
        ];
      }
      return getDefaultTimeSlots();
    }
  );
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  
  // Memoize input handlers for better performance
  const handleInputChange = useCallback((field: string, value: string) => {
    if (!hasUserEdited && (field === 'name' || field === 'phone')) {
      setHasUserEdited(true);
    }
    onUpdateData({ [field]: value });
  }, [onUpdateData, hasUserEdited]);

  const handleDateChange = useCallback((date: Date | undefined) => {
    if (date) {
      // Fix timezone issue by using local date string
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onUpdateData({ serviceDate: `${year}-${month}-${day}` });
      setIsCalendarOpen(false);
    }
  }, [onUpdateData]);

  const handleTimeSlotChange = useCallback((value: string) => {
    logger.log('Time slot changed to:', value);
    if (value) {
      onUpdateData({ serviceTime: value });
    }
  }, [onUpdateData]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  // Add debugging to understand timing
  logger.log('🕒 ServiceForm render started');
  
  // Lazy load time slots when date changes
  useEffect(() => {
    if (hasError) return; // Skip if component is in error state
    
    try {
      const selectedDate = formData?.serviceDate;
      if (!selectedDate) {
        logger.log('🕒 No selected date, setting default slots');
        setTimeSlots(getDefaultTimeSlots());
        setIsLoadingTimeSlots(false);
        return;
      }

      logger.log('🕒 Starting time slot calculation...');
      setIsLoadingTimeSlots(true);
      
      // Use setTimeout to make the heavy computation async and show loading state
      const timeoutId = setTimeout(() => {
        try {
          logger.log('🕒 Computing time slots for date:', selectedDate);
          const serviceDayHours = getSalesHoursForDate(selectedDate);
          const slots = serviceDayHours ? generateTimeSlotsFromHours(serviceDayHours) : getDefaultTimeSlots();
          logger.log('🕒 Time slots computed:', slots?.length || 0, 'slots');
          setTimeSlots(slots || getDefaultTimeSlots());
          setIsLoadingTimeSlots(false);
          logger.log('🕒 Time slots loading complete');
        } catch (error) {
          console.error('🕒 Error computing time slots:', error);
          setHasError(true);
          setTimeSlots(getDefaultTimeSlots());
          setIsLoadingTimeSlots(false);
        }
      }, 100); // Small delay to show loading state

      return () => clearTimeout(timeoutId);
    } catch (error) {
      console.error('❌ ServiceForm: Critical error in useEffect:', error);
      setHasError(true);
      setTimeSlots(getDefaultTimeSlots());
      setIsLoadingTimeSlots(false);
    }
  }, [formData?.serviceDate, hasError]);

  // Extract opening hours processing to a separate function
  const getSalesHoursForDate = (date: string) => {
    logger.log('🕒 getSalesHoursForDate called with:', date);
    try {
      if (!date || !openingHours?.length) {
        logger.log('🕒 No date or opening hours');
        return null;
      }
      
      const dateParts = date.split('-');
      if (dateParts.length !== 3) {
        logger.log('🕒 Invalid date format:', date);
        return null;
      }
      
      const [year, month, day] = dateParts.map(Number);
      if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
        logger.log('🕒 Invalid date components:', { year, month, day });
        return null;
      }
      
      const dateObj = new Date(year, month - 1, day);
      if (isNaN(dateObj.getTime())) {
        logger.log('🕒 Invalid date object');
        return null;
      }
      
      const dayOfWeek = dateObj.getDay();
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      const serviceDept = openingHours.find(dept => dept?.Department === 'Service');
      if (!serviceDept || !serviceDept.Days) {
        logger.log('🕒 No service department found');
        return null;
      }
      
      const result = serviceDept.Days.find(day => day?.day === adjustedDay);
      logger.log('🕒 Found service hours:', result);
      return result || null;
    } catch (error) {
      console.error('🕒 Error in getSalesHoursForDate:', error);
      setHasError(true);
      return null;
    }
  };

  // Convert AM/PM time to 24-hour format
  const convertTo24Hour = (hour: number, minute: number, ampm: string) => {
    if (ampm === 'pm' && hour !== 12) return hour + 12;
    if (ampm === 'am' && hour === 12) return 0;
    return hour;
  };

  const formatTimeDisplay = (hour: number) => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
  };


  const getDefaultTimeSlots = () => {
    return language === 'fr' 
      ? [
          { value: 'early_am', label: t('form.timeSlot.early'), time: '08:00-10:00' },
          { value: 'am', label: 'AM', time: '10:00-12:00' },
          { value: 'pm', label: 'PM', time: '13:00-16:00' },
          { value: 'late_pm', label: t('form.timeSlot.late'), time: '16:00-18:00' }
        ]
      : [
          { value: 'early_am', label: 'Early', time: '8:00-10:00am' },
          { value: 'am', label: 'AM', time: '10:00-12:00pm' },
          { value: 'pm', label: 'PM', time: '1:00-4:00pm' },
          { value: 'late_pm', label: 'Late', time: '4:00-6:00pm' }
        ];
  };

  // Generate time slots from hours data (moved after getDefaultTimeSlots)
  const generateTimeSlotsFromHours = (serviceDayHours: any) => {
    // Check if service is closed
    if (serviceDayHours.opening === '00:00' && serviceDayHours.closing === '00:00') {
      return getDefaultTimeSlots();
    }
    
    // Parse opening/closing times
    const openingHour = parseInt(serviceDayHours.opening.split(':')[0]);
    const openingMinute = parseInt(serviceDayHours.opening.split(':')[1]);
    const closingHour = parseInt(serviceDayHours.closing.split(':')[0]);
    const closingMinute = parseInt(serviceDayHours.closing.split(':')[1]);
    
    const openingHour24 = convertTo24Hour(openingHour, openingMinute, serviceDayHours.opening_am_pm);
    const closingHour24 = convertTo24Hour(closingHour, closingMinute, serviceDayHours.closing_am_pm);
    
    // Generate 4 equal time slots
    const totalMinutes = (closingHour24 * 60 + closingMinute) - (openingHour24 * 60 + openingMinute);
    const slots = [];
    let currentStart = openingHour24 * 60 + openingMinute;
    
    for (let i = 0; i < 4; i++) {
      const slotStart = currentStart;
      const isLastSlot = i === 3;
      const slotEnd = isLastSlot 
        ? closingHour24 * 60 + closingMinute
        : currentStart + Math.floor((totalMinutes - (currentStart - (openingHour24 * 60 + openingMinute))) / (4 - i));
      
      const startHour = Math.floor(slotStart / 60);
      const endHour = Math.floor(slotEnd / 60);
      const timeRange = `${formatTimeDisplay(startHour)}-${formatTimeDisplay(endHour)}`;
      
      slots.push({
        value: ['early_am', 'am', 'pm', 'late_pm'][i],
        label: language === 'fr' ? [t('form.timeSlot.early'), 'AM', 'PM', t('form.timeSlot.late')][i] : ['Early', 'AM', 'PM', 'Late'][i],
        time: timeRange
      });
      
      currentStart = slotEnd;
    }
    
    return slots;
  };

  const getTimeSlotDisplayText = (value: string) => {
    const slot = timeSlots.find(slot => slot.value === value);
    return slot ? `${slot.label} (${slot.time})` : value;
  };

  const handleFormSubmit = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const name = nameInputRef.current?.value || '';
      const phone = phoneInputRef.current?.value || '';
      
      // Get the display text for the selected time slot
      const timeDisplayText = getTimeSlotDisplayText(formData.serviceTime || '');
      
      // Create a comprehensive service message for the lead
      const serviceMessage = language === 'fr'
        ? `Demande de rendez-vous service - ${name} a demandé un rendez-vous service pour le ${formData.serviceDate || ''} durant ${timeDisplayText}. Contact: ${phone} / ${formData.email || ''}`
        : `Service Appointment Request - ${name} has requested a service appointment for ${formData.serviceDate || ''} during ${timeDisplayText}. Contact: ${phone} / ${formData.email || ''}`;

      // Create service-specific interest data with explicit department override
      const serviceInterestData = {
        message: serviceMessage,
        department: 'Service',
        serviceDetails: {
          date: formData.serviceDate || '',
          time: timeDisplayText,
          email: formData.email || ''
        }
      };

      const submissionData = createStandardizedFormSubmission(
        {
          name,
          phone,
          email: formData.email || '',
          serviceDate: formData.serviceDate || '',
          serviceTime: timeDisplayText,
          interest: formData.interest || ''
        },
        'service',
        formatPhoneNumber,
        serviceInterestData
      );
      
      if (onSubmit) {
        await onSubmit(submissionData);
      }
      
      onUpdateData({ ...formData, name, phone });
    } catch (error) {
      // Form submission error
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    const name = nameInputRef.current?.value || '';
    const phone = phoneInputRef.current?.value || '';
    
    switch (currentStep) {
      case 0:
        return formData.serviceDate?.trim().length > 0 &&
               formData.serviceTime?.trim().length > 0;
      case 1:
        return name.trim().length > 0 && 
               phone.trim().length > 0 &&
               formData.email?.trim().length > 0 && 
               formData.email.includes('@');
      default:
        return false;
    }
  };

  // Check if a date should be disabled (past dates or when dealer is closed)
  const isDateDisabled = (date: Date) => {
    // Always disable past dates
    if (date < new Date()) {
      return true;
    }
    
    // Check if dealer is closed on this day
    if (openingHours && openingHours.length > 0) {
      const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to 1=Monday, 7=Sunday
      
      // Find the service department hours for this day
      const serviceDept = openingHours.find(dept => dept.Department === 'Service');
      if (serviceDept) {
        const dayHours = serviceDept.Days.find(day => day.day === adjustedDay);
        // If no hours found for this day or both opening and closing are '00:00', it's closed
        if (!dayHours || (dayHours.opening === '00:00' && dayHours.closing === '00:00')) {
          return true;
        }
      }
    }
    
    return false;
  };

  const getSelectedDate = () => {
    if (formData.serviceDate) {
      // Fix timezone issue by parsing the date components directly
      const [year, month, day] = formData.serviceDate.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    return undefined;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-2">
            {/* Opening Hours Table - Only show Service department */}
            {openingHours && openingHours.length > 0 && (
              <div className="mb-0.5">
                <CollapsibleOpeningHours 
                  openingHours={openingHours.filter(dept => dept.Department === 'Service')} 
                  triggerLabel={t('hours.service')}
                />
              </div>
            )}
            
            {/* Hidden Interest field */}
            <input type="hidden" name="interest" value={formData.interest || ''} />
            
            {/* Hidden topcarid field */}
            <input 
              id="topcarid" 
              name="topcarid" 
              type="hidden" 
              value={(() => {
                try {
                  const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
                  return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
                } catch {
                  return '';
                }
              })()}
            />
            
            {/* Date field */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-700">{t('form.serviceDate')}</Label>
              <div className="d2cbot-datepicker-wrapper">
                <IsolatedDatePicker
                  value={formData.serviceDate}
                  onChange={(date) => handleInputChange('serviceDate', date)}
                  disabled={isDateDisabled}
                  placeholder={t('form.selectDate')}
                  className="w-full"
                />
              </div>
            </div>
            
            {/* Time Slot Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-700">{t('form.serviceTime')}</Label>
                <span className="text-xs text-gray-500 italic">
                  {t('form.timeSlot.specificTime')}
                </span>
              </div>
              <div className="w-full">
                <IsolatedTimePicker
                  value={formData.serviceTime}
                  onChange={(time) => handleInputChange('serviceTime', time)}
                  timeSlots={timeSlots}
                  variant="buttons"
                  loading={isLoadingTimeSlots}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-2">
            {/* Side by side fields */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="name" className="text-xs text-gray-700">{t('form.fullName')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  defaultValue={localFormData.name || ''}
                  ref={nameInputRef}
                  onChange={() => handleInputChange('name', nameInputRef.current?.value || '')}
                  placeholder={t('form.fullName')}
                  className="h-7 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone" className="text-xs text-gray-700">{t('form.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  defaultValue={localFormData.phone || ''}
                  ref={phoneInputRef}
                  onChange={() => handleInputChange('phone', phoneInputRef.current?.value || '')}
                  placeholder="(555) 555-1212"
                  className="h-7 text-sm"
                />
              </div>
            </div>
            
            {/* Email field at the bottom */}
            <FormField
              id="email"
              name="email"
              label={t('form.email')}
              type="email"
              autoComplete="email"
              value={formData.email || ''}
              onChange={(value) => handleInputChange('email', value)}
              placeholder="exemple@email.com"
            />
          </div>
        );
      default:
        return null;
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  logger.log('🕒 ServiceForm about to render');

  // Error fallback UI
  if (hasError) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">{t('form.error') || 'An error occurred loading the form'}</p>
        <Button onClick={() => setHasError(false)} size="sm">
          {t('form.retry') || 'Retry'}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-0.9">
      <div className="mb-2">
        {renderStep()}
      </div>
      
      <div className="flex items-center justify-between">
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            className="h-6 px-2 text-xs"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            {t('form.back')}
          </Button>
        )}
        
        <div className="flex items-center space-x-1">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div
              key={index}
              className={`w-1.5 h-1.5 rounded-full ${
                index === currentStep
                  ? 'bg-black'
                  : index < currentStep
                  ? 'bg-gray-400'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        
        {!isLastStep ? (
          <Button
            type="button"
            size="sm"
            onClick={handleNext}
            disabled={!isStepValid()}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {t('form.next')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleFormSubmit}
            disabled={!isStepValid() || isSubmitting}
            className="h-6 px-2 text-xs bg-black hover:bg-gray-800"
          >
            {isSubmitting ? t('form.submitting') : t('form.submit')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ServiceForm;
